import {
  AloftArea,
  AloftAreaJSON,
  AloftDate,
  AloftDateJSON,
  RecommendedCompsFilters,
} from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

// Interpolates green (#22c55e) → red (#ef4444) across tier 1–8
const tierColor = (tier: number, maxTier = 8): string => {
  const t = Math.max(0, Math.min(1, (tier - 1) / (maxTier - 1)));
  const r = Math.round(34 + (239 - 34) * t);
  const g = Math.round(197 + (68 - 197) * t);
  const b = Math.round(94 + (68 - 94) * t);
  return `rgb(${r}, ${g}, ${b})`;
};

type FilterPillProps = {
  label: string;
  value: string;
};

const FilterPill = ({ label, value }: FilterPillProps) => (
  <Block
    borderRadius="999px"
    border="1px solid"
    borderColor="strokeDefault"
    backgroundColor="surface2"
    padding="4px 12px"
  >
    <Typography variant="body2">
      <strong>{label}:</strong> {value}
    </Typography>
  </Block>
);

// The API returns plain JSON, so min/max arrive as AloftAreaJSON even though
// the Zod type says AloftArea. Handle both defensively.
type MaybeAreaRange =
  | { min?: AloftArea | AloftAreaJSON | null; max?: AloftArea | AloftAreaJSON | null }
  | null
  | undefined;
type MaybeNumberRange = { min?: number | null; max?: number | null } | null | undefined;
type MaybeDateRange =
  | { min?: AloftDate | AloftDateJSON | null; max?: AloftDate | AloftDateJSON | null }
  | null
  | undefined;

const toSqft = (v: AloftArea | AloftAreaJSON | null | undefined): number | null => {
  if (v == null) return null;
  if (v instanceof AloftArea) return Math.round(v.getUnitAmount('sqft'));
  return Math.round(AloftArea.fromNumber(v.unitAmount, v.unit).getUnitAmount('sqft'));
};

const formatAreaRange = (range: MaybeAreaRange): string | null => {
  if (!range) return null;
  const min = toSqft(range.min);
  const max = toSqft(range.max);
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min.toLocaleString()} – ${max.toLocaleString()} sqft`;
  if (min != null) return `≥ ${min.toLocaleString()} sqft`;
  return `≤ ${max!.toLocaleString()} sqft`;
};

const formatNumberRange = (range: MaybeNumberRange): string | null => {
  if (!range) return null;
  const { min, max } = range;
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min} – ${max}`;
  if (min != null) return `≥ ${min}`;
  return `≤ ${max!}`;
};

const toDateString = (v: AloftDate | AloftDateJSON | null | undefined): string | null => {
  if (v == null) return null;
  const iso = v instanceof AloftDate ? v.toIsoString() : v.isoString;
  return iso ?? null;
};

const formatDateRange = (range: MaybeDateRange): string | null => {
  if (!range) return null;
  const min = toDateString(range.min);
  const max = toDateString(range.max);
  if (!min && !max) return null;
  if (min && max) return `${min} – ${max}`;
  if (min) return `≥ ${min}`;
  return `≤ ${max!}`;
};

type RegressionFiltersProps = {
  filters: RecommendedCompsFilters;
  filterTier: number;
};

export const RegressionFilters = ({ filters, filterTier }: RegressionFiltersProps) => {
  const pills: FilterPillProps[] = [];

  if (filters.propertySubType)
    pills.push({ label: 'Property Type', value: filters.propertySubType.join(', ') });

  const gla = formatAreaRange(filters.gla);
  if (gla) pills.push({ label: 'GLA', value: gla });

  const lotSize = formatAreaRange(filters.lotSize);
  if (lotSize) pills.push({ label: 'Lot Size', value: lotSize });

  const bedrooms = formatNumberRange(filters.bedrooms);
  if (bedrooms) pills.push({ label: 'Bedrooms', value: bedrooms });

  const fullBaths = formatNumberRange(filters.fullBaths);
  if (fullBaths) pills.push({ label: 'Full Baths', value: fullBaths });

  const halfBaths = formatNumberRange(filters.halfBaths);
  if (halfBaths) pills.push({ label: 'Half Baths', value: halfBaths });

  const garageSpaces = formatNumberRange(filters.garageSpaces);
  if (garageSpaces) pills.push({ label: 'Garage Spaces', value: garageSpaces });

  const yearBuilt = formatNumberRange(filters.yearBuilt);
  if (yearBuilt) pills.push({ label: 'Year Built', value: yearBuilt });

  const saleDate = formatDateRange(filters.saleDate);
  if (saleDate) pills.push({ label: 'Sale Date', value: saleDate });

  const color = tierColor(filterTier);

  return (
    <Flex flexDirection="column" gap="12px">
      <Flex alignItems="center" gap="12px">
        <Typography variant="h4">Search Filters Used</Typography>
        <Flex alignItems="center" gap="6px">
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              backgroundColor: color,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" color="textSecondary">
            Tier {filterTier}/8
          </Typography>
        </Flex>
      </Flex>

      {pills.length > 0 ? (
        <Flex flexWrap="wrap" gap="8px">
          {pills.map((pill) => (
            <FilterPill key={pill.label} label={pill.label} value={pill.value} />
          ))}
        </Flex>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No filter ranges applied.
        </Typography>
      )}
    </Flex>
  );
};
